import type { ComponentType, SVGProps } from "react";
import {
  ArrowInboxIcon,
  BagIcon,
  BellIcon,
  CircleBanIcon,
  ConnectorsIcon,
  GiftcardIcon,
  GiroCardsIcon,
  GroupIcon,
  PageLockIcon,
  PaintBucketIcon,
  PaperPlaneIcon,
  PeopleIcon,
  PluginIcon,
  MeetingsIcon,
  ScorecardsIcon,
  SettingsGearAltIcon,
  ShieldIcon,
  TalentPoolsIcon,
} from "@/components/icons/settings";
import { CONVERSATIONS_ROUTES } from "@/features/conversations/lib/routes";
import { FEATURE_FLAGS } from "@/lib/feature-flags";

export type SettingsIconComponent = ComponentType<
  SVGProps<SVGSVGElement> & { size?: number }
>;

export interface SettingsNavItem {
  href: string;
  icon: SettingsIconComponent;
  label: string;
  upgradeIcon?: boolean;
  disabled?: boolean;
  soon?: boolean;
  feature?: "conversation_capture";
}

export type SettingsNavSubscription = {
  plan?: { features?: Record<string, boolean> } | null;
} | null | undefined;

export function isNavItemVisible(
  item: SettingsNavItem,
  subscription: SettingsNavSubscription,
): boolean {
  if (!item.feature) return true;
  return subscription?.plan?.features?.[item.feature] === true;
}

export const SETTINGS_PERSONAL_NAV: SettingsNavItem[] = [
  { href: "/settings/profile", icon: PeopleIcon, label: "Profile" },
  { href: "/settings/appearance", icon: PaintBucketIcon, label: "Appearance" },
  {
    href: "/settings/connected-accounts",
    icon: ConnectorsIcon,
    label: "Connected Accounts",
  },
  { href: "/settings/notifications", icon: BellIcon, label: "Notifications" },
  {
    href: CONVERSATIONS_ROUTES.personalSettings,
    icon: MeetingsIcon,
    label: "My meetings",
    feature: "conversation_capture",
  },
  { href: "/settings/my-exports", icon: ArrowInboxIcon, label: "My Exports" },
  {
    href: "/settings/referrals",
    icon: GiftcardIcon,
    label: "Refer & earn",
    disabled: !FEATURE_FLAGS.referrals,
    soon: !FEATURE_FLAGS.referrals,
  },
];

export const SETTINGS_ORG_NAV: SettingsNavItem[] = [
  { href: "/settings/general", icon: SettingsGearAltIcon, label: "General" },
  { href: "/settings/users", icon: GroupIcon, label: "Users" },
  { href: "/settings/employers", icon: BagIcon, label: "Employers" },
  ...(FEATURE_FLAGS.scorecards
    ? [{ href: "/settings/scorecards", icon: ScorecardsIcon, label: "Scorecards" }]
    : []),
  {
    href: CONVERSATIONS_ROUTES.orgSettings,
    icon: MeetingsIcon,
    label: "Notetaker",
    feature: "conversation_capture",
  },
  {
    href: "/settings/plans-billing",
    icon: GiroCardsIcon,
    label: "Plans & Billing",
  },
  { href: "/settings/security", icon: ShieldIcon, label: "Security" },
  { href: "/settings/privacy", icon: PageLockIcon, label: "Privacy" },
  { href: "/settings/sequences", icon: PaperPlaneIcon, label: "Sequences" },
  { href: "/settings/exports", icon: ArrowInboxIcon, label: "Export History" },
  { href: "/settings/exclusions", icon: CircleBanIcon, label: "Exclusions" },
  { href: "/settings/talent-pools", icon: TalentPoolsIcon, label: "Talent pools" },
  {
    href: "/settings/integrations",
    icon: PluginIcon,
    label: "Integrations",
    upgradeIcon: true,
  },
];

export function isSettingsNavActive(pathname: string, href: string): boolean {
  if (href === "/settings/profile") {
    return pathname === "/settings" || pathname === "/settings/profile";
  }
  return pathname.startsWith(href);
}
